import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import { useNavigate } from "react-router-dom";
import colors from "../../../assets/theme/colors";
import { subscribeToKnowledgeItems } from "../../../features/knowledgeItems";

const STRUCTURED_GROUPS = [
  {
    id: "risk",
    label: "Risks",
    icon: PsychologyAltOutlinedIcon,
  },
  {
    id: "trigger",
    label: "Triggers",
    icon: PsychologyAltOutlinedIcon,
  },
  {
    id: "strategy",
    label: "Strategies",
    icon: PsychologyAltOutlinedIcon,
  },
  {
    id: "preference",
    label: "Preferences",
    icon: ArticleOutlinedIcon,
  },
  {
    id: "goal",
    label: "Goals",
    icon: FlagOutlinedIcon,
  },
  {
    id: "instruction",
    label: "Instructions",
    icon: MedicationOutlinedIcon,
  },
  {
    id: "support",
    label: "Supports",
    icon: PsychologyAltOutlinedIcon,
  },
  {
    id: "condition",
    label: "Conditions",
    icon: MedicationOutlinedIcon,
  },
  {
    id: "contact",
    label: "Contacts",
    icon: ContactPhoneOutlinedIcon,
  },
  {
    id: "routine",
    label: "Routines",
    icon: ArticleOutlinedIcon,
  },
  {
    id: "baseline",
    label: "Baselines",
    icon: ArticleOutlinedIcon,
  },
  {
    id: "note",
    label: "Notes",
    icon: ArticleOutlinedIcon,
  },
  {
    id: "other",
    label: "Other",
    icon: DescriptionOutlinedIcon,
  },
];

export const getCareKnowledgeUpdatedTime = (item = {}) => {
  const value = item.updatedAt || item.capturedAt || item.createdAt;
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const groupCareKnowledgeItems = (items = []) => {
  const groups = STRUCTURED_GROUPS.map((group) => ({
    ...group,
    items: [],
  }));
  const groupById = new Map(groups.map((group) => [group.id, group]));

  items.forEach((item) => {
    const group = groupById.get(item.knowledgeType || item.factType) || groupById.get("other");
    group.items.push(item);
  });

  return groups
    .map((group) => ({
      ...group,
      items: [...group.items].sort((left, right) => getCareKnowledgeUpdatedTime(right) - getCareKnowledgeUpdatedTime(left)),
    }))
    .filter((group) => group.items.length > 0);
};

const StructuredInformationSection = ({
  childId = "",
  displayName = "",
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(childId));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!childId) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");
    return subscribeToKnowledgeItems({
      childId,
      onItems: (nextItems) => {
        setItems(nextItems);
        setLoading(false);
      },
      onError: () => {
        setError("Care Knowledge could not load right now.");
        setLoading(false);
      },
    });
  }, [childId]);

  const groups = useMemo(() => groupCareKnowledgeItems(items), [items]);

  if (!childId || (!loading && !error && groups.length === 0)) {
    return null;
  }

  return (
    <Box
      sx={{
        borderRadius: "24px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, sm: 1.5 },
          py: 1.15,
          borderBottom: `1px solid ${alpha(colors.semantic.profileFlow.cardBorder, 0.9)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.8}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontSize: { xs: "1rem", sm: "1.06rem" }, fontWeight: 900, color: colors.semantic.ink }}>
              Care Knowledge
            </Typography>
            <Typography sx={{ mt: 0.2, fontSize: "0.82rem", lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.66) }}>
              Reviewed goals, supports, contacts, routines, and important details saved for {displayName || "this profile"}.
            </Typography>
          </Box>
          {groups.length > 0 ? (
            <Button
              size="small"
              onClick={() => navigate("/care-knowledge", { state: { childId } })}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                borderRadius: "999px",
                color: colors.brand.deep,
                fontWeight: 850,
                textTransform: "none",
              }}
            >
              View all
            </Button>
          ) : null}
        </Stack>
      </Box>

      <Stack spacing={1} sx={{ px: { xs: 1.25, sm: 1.5 }, py: 1.25 }}>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} thickness={4} />
            <Typography sx={{ fontSize: "0.84rem", color: alpha(colors.semantic.ink, 0.66) }}>
              Loading Care Knowledge...
            </Typography>
          </Stack>
        ) : null}

        {error ? (
          <Typography sx={{ fontSize: "0.84rem", color: colors.semantic.warning }}>
            {error}
          </Typography>
        ) : null}

        {groups.map((group) => {
          const GroupIcon = group.icon;
          const previewItems = group.items.slice(0, 3);
          const hiddenCount = group.items.length - previewItems.length;

          return (
            <Box
              key={group.id}
              sx={{
                borderRadius: "18px",
                border: `1px solid ${alpha(colors.semantic.profileFlow.cardBorder, 0.9)}`,
                bgcolor: alpha("#FFFFFF", 0.78),
                p: 1,
              }}
            >
              <Stack spacing={0.85}>
                <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "12px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(colors.brand.tint, 0.32),
                        color: colors.brand.deep,
                        flexShrink: 0,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 900, color: colors.semantic.ink }}>
                      {group.label}
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    label={group.items.length}
                    sx={{
                      height: 24,
                      borderRadius: "999px",
                      bgcolor: alpha(colors.brand.tint, 0.22),
                      color: colors.brand.deep,
                      fontWeight: 850,
                    }}
                  />
                </Stack>

                <Stack spacing={0.65}>
                  {previewItems.map((item) => (
                    <Box key={item.id || `${group.id}-${item.title}`}>
                      <Typography sx={{ fontSize: "0.84rem", fontWeight: 850, color: colors.semantic.ink, lineHeight: 1.25 }}>
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.15,
                          fontSize: "0.79rem",
                          lineHeight: 1.35,
                          color: alpha(colors.semantic.ink, 0.68),
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.summary || item.content}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {hiddenCount > 0 ? (
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: colors.brand.deep }}>
                    + {hiddenCount} more
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default StructuredInformationSection;
